import { forwardRef, useEffect, useMemo, useState, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import * as DialogPrimitive from "@radix-ui/react-dialog";
import { api } from "@/lib/api";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { Button } from "@/components/ui/button";
import {
  Bell, Check, Clock, CheckSquare, MessageSquare, Receipt, AlertTriangle, Layers, Briefcase, BookOpen, X,
} from "lucide-react";

// 215: v DOM jsou zvonečky dva (boční panel + mobilní horní lišta) — sdílí jeden dotaz
// a ptají se jen na viditelné kartě; po návratu na kartu hned.
const EMPTY = { items: [], unread: 0 };
const store = { data: EMPTY, subs: new Set(), timer: null, inflight: null };
const emit = () => store.subs.forEach((fn) => fn(store.data));
const setStore = (data) => { store.data = data; emit(); };
// force = po změně (přečteno) nový dotaz; odpověď staršího, ještě běžícího dotazu se zahodí
function refresh(force = false) {
  if (store.inflight && !force) return store.inflight;
  const p = api.get("/notifications").then((r) => { if (store.inflight === p) setStore(r.data); }).catch(() => {})
    .finally(() => { if (store.inflight === p) store.inflight = null; });
  store.inflight = p;
  return p;
}
const onVisible = () => { if (document.visibilityState === "visible") refresh(); };
function subscribe(fn) {
  store.subs.add(fn);
  if (store.subs.size === 1) {
    refresh();
    store.timer = setInterval(onVisible, 30000);
    document.addEventListener("visibilitychange", onVisible);
  } else fn(store.data);
  return () => {
    store.subs.delete(fn);
    if (store.subs.size) return;
    clearInterval(store.timer);
    document.removeEventListener("visibilitychange", onVisible);
    store.data = EMPTY;                 // po odhlášení nezůstanou upozornění předchozího účtu
    store.inflight = null;              // ani odpověď, která ještě doběhne
  };
}
// 234: přečtené je až to, na co uživatel klikne (ne celé otevření panelu).
// ids = null → všechna. Lokálně hned, server na pozadí; nepovede-li se, srovná to další dotaz.
function markRead(ids) {
  const set = ids ? new Set(ids) : null;
  const items = store.data.items.map((n) => (!set || set.has(n.id) ? { ...n, read: true } : n));
  setStore({ items, unread: items.filter((n) => !n.read).length });
  api.post("/notifications/read", ids ? { ids } : {}).catch(() => refresh(true));
}

// Stejná upozornění (Ivan Benchyk nebyl odhlášen 2×) = jeden řádek s počtem.
export function groupNotifications(items) {
  const out = []; const byKey = new Map();
  for (const n of items) {
    const key = [n.type, n.title, n.body, n.link || n.task_id || ""].join("\u0000");
    const g = byKey.get(key);
    // 244: `unread` = kolik z řádku je nepřečtených — počítadla filtrů nesmí počítat i přečtené opakování
    if (g) { g.ids.push(n.id); g.count += 1; g.unread += n.read ? 0 : 1; g.read = g.read && !!n.read; continue; }
    const row = { ...n, ids: [n.id], count: 1, unread: n.read ? 0 : 1, read: !!n.read };
    byKey.set(key, row); out.push(row);
  }
  return out;
}

// „před 12 min", „před 4 h", „včera 9:40", „11. 10."
export function whenText(iso, now = new Date()) {
  const d = new Date(iso);
  if (isNaN(d)) return "";
  const min = Math.floor((now - d) / 60000);
  if (min < 1) return "teď";
  if (min < 60) return `před ${min} min`;
  const hm = `${d.getHours()}:${String(d.getMinutes()).padStart(2, "0")}`;
  const day = (x) => new Date(x.getFullYear(), x.getMonth(), x.getDate()).getTime();
  const days = Math.round((day(now) - day(d)) / 86400000);
  if (days === 0) return `před ${Math.floor(min / 60)} h`;
  if (days === 1) return `včera ${hm}`;
  return `${d.getDate()}. ${d.getMonth() + 1}.${d.getFullYear() !== now.getFullYear() ? ` ${d.getFullYear()}` : ""}`;
}

const FILTERS = [
  { key: "vse", label: "Vše" },
  { key: "todo", label: "K vyřízení" },
  { key: "ukoly", label: "Úkoly" },
  { key: "dochazka", label: "Docházka" },
  { key: "denik", label: "Deník" },
  { key: "komentare", label: "Komentáře" },
];
// 238: datum v textu krátce jako 13.10.26 (starší upozornění mají ze serveru 2026-10-13)
export const shortDates = (t) => String(t || "").replace(/\b(\d{4})-(\d{2})-(\d{2})\b/g, (_, y, m, d) => `${+d}.${+m}.${y.slice(2)}`);
// 244: „K vyřízení" = jen nevyřízené (nepřečtené) — po kliknutí z filtru zmizí a zůstane ve „Vše".
export const inFilter = (n, f) => f === "vse" || (f === "todo" ? !!n.todo && !n.read : n.cat === f);
// 253: počítá řádky, které uživatel vidí — sloučená skupina (deník 17×) je jedna věc, i když má víc nepřečtených
export const filterCount = (rows, f) => rows.filter((n) => inFilter(n, f) && n.unread > 0).length;
export const unreadRows = (items) => groupNotifications(items || []).filter((n) => n.unread > 0).length;
// Druhy (Úkoly, Docházka…) jen když v nich něco je — „Vše" a „K vyřízení" vždy.
export const visibleFilters = (rows) => FILTERS.filter((f) => f.key === "vse" || f.key === "todo" || rows.some((n) => n.cat === f.key));

function TypeIcon({ n, small }) {
  const t = n.type || "";
  const Icon = n.cat === "dochazka" ? Clock : n.cat === "komentare" ? MessageSquare
    : t === "diary" ? BookOpen : n.cat === "ukoly" ? CheckSquare : t === "sub_invoice" ? Receipt
    : t === "budget" ? AlertTriangle : t.startsWith("pasport") ? Layers : t === "project" ? Briefcase : Bell;
  const tone = n.todo && !n.read ? "bg-brand/10 text-brand" : "bg-secondary text-muted-foreground";
  return <span className={`${small ? "h-7 w-7" : "h-9 w-9"} rounded-full grid place-items-center shrink-0 ${tone}`}><Icon size={small ? 14 : 17} aria-hidden="true" /></span>;
}

// Velikosti: telefon větší (prst), počítač drobnější jako v návrhu (238)
const SZ = {
  phone: { head: "px-5 pt-4 pb-3", h2: "text-xl", all: "text-sm", chips: "px-5 pb-3", chip: "h-9 px-3.5 text-[13.5px]", cnt: "min-w-[18px] h-[18px] text-[11px]",
    list: "px-2.5 py-1.5", item: "gap-3 px-2.5 py-2.5", title: "text-[14.5px]", body: "block text-[13.5px]", time: "text-xs mt-1", mark: "right-2.5 top-2.5 h-8 w-8" },
  pc: { head: "px-4 pt-3.5 pb-2.5", h2: "text-[17px]", all: "text-[13px]", chips: "px-4 pb-2.5", chip: "h-7 px-3 text-[12.5px]", cnt: "min-w-[16px] h-4 text-[10px]",
    list: "px-2 py-1", item: "gap-2.5 px-2 py-2", title: "text-[13.5px]", body: "text-[12.5px] line-clamp-2", time: "text-[11px] mt-0.5", mark: "right-2 top-2 h-7 w-7" },
};

function Panel({ data, mobile, onClose }) {
  const nav = useNavigate();
  const z = mobile ? SZ.phone : SZ.pc;
  const [pick, setFilter] = useState("vse");
  const rows = useMemo(() => groupNotifications(data.items), [data.items]);
  const filters = visibleFilters(rows);
  const filter = filters.some((f) => f.key === pick) ? pick : "vse";
  const shown = rows.filter((n) => inFilter(n, filter));
  const count = (f) => filterCount(rows, f);

  const open = (n) => {
    onClose();
    if (!n.read) markRead(n.ids);
    const to = n.link || (n.task_id ? `/ukoly/${n.task_id}` : null);
    if (to) nav(to);
  };

  return (
    <div className="flex flex-col min-h-0" data-testid="notif-panel">
      <div className={`flex items-center gap-3 ${z.head}`}>
        <h2 className={`flex-1 font-heading font-extrabold tracking-tight ${z.h2}`}>Upozornění</h2>
        <button type="button" onClick={() => markRead(null)} disabled={!data.unread} data-testid="notif-read-all"
          className={`${z.all} font-bold text-brand disabled:text-muted-foreground disabled:opacity-60 hover:underline whitespace-nowrap`}>
          Označit vše
        </button>
        {mobile && (
          <button type="button" onClick={onClose} aria-label="Zavřít"
            className="h-9 w-9 rounded-full bg-secondary grid place-items-center shrink-0"><X size={17} /></button>
        )}
      </div>
      {/* Posuvný filtr: okraj a prolnutí na koncích, ať je vidět, že řádek pokračuje */}
      <div role="group" aria-label="Filtr upozornění"
        className={`flex gap-1.5 ${z.chips} overflow-x-auto shrink-0 [scrollbar-width:none] [&::-webkit-scrollbar]:hidden after:content-[''] after:shrink-0 after:w-5 [mask-image:linear-gradient(90deg,transparent_0,#000_18px,#000_calc(100%-26px),transparent_100%)]`}>
        {filters.map((f) => {
          const c = f.key === "vse" ? 0 : count(f.key);
          const on = filter === f.key;
          return (
            <button key={f.key} type="button" onClick={() => setFilter(f.key)} aria-pressed={on} data-testid={`notif-filter-${f.key}`}
              className={`${z.chip} rounded-full font-bold whitespace-nowrap inline-flex items-center gap-1.5 transition-colors ${on ? "bg-foreground text-background" : "bg-secondary text-muted-foreground hover:text-foreground"}`}>
              {f.label}
              {c > 0 && <span className={`${z.cnt} px-1 rounded-full bg-brand text-white grid place-items-center tabular-nums`}>{c}</span>}
            </button>
          );
        })}
      </div>
      <div className={`flex-1 min-h-0 overflow-y-auto border-t border-border ${z.list} overscroll-contain`}>
        {shown.length === 0 && (
          <div className="px-5 py-10 text-center text-sm text-muted-foreground">
            <div className="text-base font-bold text-foreground mb-1">Nic tu není</div>
            {filter === "vse" ? "Zatím nemáte žádná upozornění." : filter === "todo" ? "Všechno je vyřízené." : "V tomhle filtru nemáte žádná upozornění."}
          </div>
        )}
        {shown.map((n) => (
          <div key={n.id} className="group relative">
            <button type="button" onClick={() => open(n)} data-testid="notif-item"
              className={`w-full text-left flex ${z.item} items-start rounded-[14px] hover:bg-secondary transition-colors ${n.read ? "opacity-60" : ""}`}>
              <TypeIcon n={n} small={!mobile} />
              <span className="min-w-0 flex-1">
                <span className={`flex items-center gap-1.5 font-bold ${z.title} min-w-0`}>
                  <span className="min-w-0 break-words">{n.title}</span>
                  {n.count > 1 && <span className="shrink-0 text-[11px] font-extrabold bg-secondary rounded-md px-1.5 py-px text-muted-foreground" data-testid="notif-count">{n.count}×</span>}
                  {!n.read && <span className="ml-auto h-2 w-2 rounded-full bg-brand shrink-0 md:group-hover:invisible" aria-label="Nepřečtené" />}
                </span>
                {n.body && <span className={`${z.body} text-muted-foreground mt-0.5 break-words`} title={mobile ? undefined : shortDates(n.body)}>{shortDates(n.body)}</span>}
                <span className={`block ${z.time} text-muted-foreground tabular-nums`} title={new Date(n.created_at).toLocaleString("cs-CZ")}>{whenText(n.created_at)}</span>
              </span>
            </button>
            {!n.read && (
              <button type="button" onClick={() => markRead(n.ids)} aria-label="Označit jako přečtené" title="Označit jako přečtené"
                data-testid="notif-mark"
                className={`hidden md:group-hover:grid md:focus-visible:grid absolute ${z.mark} place-items-center rounded-full bg-card border border-border text-muted-foreground hover:text-foreground`}>
                <Check size={mobile ? 15 : 13} />
              </button>
            )}
          </div>
        ))}
      </div>
    </div>
  );
}

// forwardRef: Radix (Popover/Dialog Trigger asChild) z ref počítá polohu panelu
const Trigger = forwardRef(function Trigger({ data, open, triggerClassName, iconSize, ...rest }, ref) {
  const unread = useMemo(() => unreadRows(data.items), [data.items]);   // 253: stejně jako řádky v panelu
  return (
    <Button ref={ref} variant="ghost" size="icon" data-testid="notif-bell" aria-label={unread > 0 ? `Upozornění, ${unread} nová` : "Upozornění"}
      className={`relative ${open ? "bg-secondary" : ""} ${triggerClassName}`} {...rest}>
      <Bell size={iconSize} />
      {unread > 0 && (
        <span data-testid="notif-badge" className="absolute -top-0.5 -right-0.5 z-10 min-w-[18px] h-[18px] px-1 bg-brand text-white text-[10px] font-bold rounded-full flex items-center justify-center ring-2 ring-card">
          {unread > 99 ? "99+" : unread}
        </span>
      )}
    </Button>
  );
});

// placement: "side" = boční lišta na počítači (panel vedle lišty, zarovnaný ke zvonečku dole),
// "top" = horní lišta na telefonu (panel sjede od horního okraje přes celou šířku). 234
export function NotificationBell({ triggerClassName = "", iconSize = 18, placement = "side" } = {}) {
  const [data, setData] = useState(store.data);
  const [open, setOpen] = useState(false);
  useEffect(() => subscribe(setData), []);
  const onOpen = useCallback((o) => { setOpen(o); if (o) refresh(true); }, []);
  const close = useCallback(() => setOpen(false), []);

  if (placement === "top") {
    return (
      <DialogPrimitive.Root open={open} onOpenChange={onOpen}>
        <DialogPrimitive.Trigger asChild>
          <Trigger data={data} open={open} triggerClassName={triggerClassName} iconSize={iconSize} />
        </DialogPrimitive.Trigger>
        <DialogPrimitive.Portal>
          <DialogPrimitive.Overlay className="fixed inset-0 z-50 bg-black/40 data-[state=open]:animate-in data-[state=open]:fade-in-0 data-[state=closed]:animate-out data-[state=closed]:fade-out-0 motion-reduce:animate-none" />
          <DialogPrimitive.Content aria-describedby={undefined}
            className="fixed inset-x-0 top-0 z-50 max-h-[88dvh] flex flex-col bg-card text-foreground rounded-b-[28px] shadow-2xl pt-[env(safe-area-inset-top)] outline-none data-[state=open]:animate-in data-[state=open]:slide-in-from-top data-[state=closed]:animate-out data-[state=closed]:slide-out-to-top duration-300 motion-reduce:animate-none">
            <DialogPrimitive.Title className="sr-only">Upozornění</DialogPrimitive.Title>
            <Panel data={data} mobile onClose={close} />
            <div className="mx-auto mt-1 mb-2.5 h-[5px] w-10 rounded-full bg-border shrink-0" aria-hidden="true" />
          </DialogPrimitive.Content>
        </DialogPrimitive.Portal>
      </DialogPrimitive.Root>
    );
  }

  return (
    <Popover open={open} onOpenChange={onOpen}>
      <PopoverTrigger asChild>
        <Trigger data={data} open={open} triggerClassName={triggerClassName} iconSize={iconSize} />
      </PopoverTrigger>
      <PopoverContent side="right" align="end" sideOffset={26} collisionPadding={16}
        className="w-[380px] max-w-[calc(100vw-32px)] max-h-[min(560px,calc(100dvh-32px))] flex flex-col p-0 rounded-[20px] bg-card shadow-2xl overflow-hidden">
        <Panel data={data} onClose={close} />
      </PopoverContent>
    </Popover>
  );
}
